import { supabase } from '@tickitacka/shared';
import {
    Goal,
    GoalWithTeamInfo,
    Note,
    NoteBookmark,
    NoteWithDetails,
    TeamBase,
    Todo,
} from '../../../common/types/databaseTypes';
import { v4 as uuidv4 } from 'uuid';
import { authService } from '@/features/auth/services/authService';

// Note join table
export interface NotePath {
    team: TeamBase;
    goal?: Partial<Goal> & { team?: TeamBase };
    todo?: Partial<Todo> & { goal?: GoalWithTeamInfo };
    note: Note;
}

export interface CreateNoteInput {
    title: string;
    content?: string;
    markdown_content?: string;
    register_id: string;
    goal_id?: string;
    todo_id?: string;
    workspace_id?: string;
}

/**
 * 노트 데이터 관리에 필요한 기본 데이터 인터페이스
 *
 * @property _id - 노트 ID
 * @property title - 노트 제목
 * @property targetId - 노트의 goal_id 또는 todo_id (캐시 관리 쿼리키에 사용)
 */
export interface InitialNoteData {
    _id: string;
    title?: string;
    targetId: string;
}

// 단축 URL 생성을 위한 인터페이스
export interface ShareLinkData {
    noteId: string;
    originalUrl: string;
}

export const noteService = {
    // Create a new note
    async createNote(params: CreateNoteInput): Promise<Note> {
        try {
            const now = new Date().toISOString();

            const { data, error } = await supabase
                .from('note')
                .insert({
                    id: uuidv4(),
                    ...params,
                    register_date: now,
                    update_date: now,
                    enable: true,
                    content: params.content || '',
                    markdown_content: params.markdown_content || '',
                })
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Failed to create note:', error);
            throw error;
        }
    },

    // Get note by ID
    async getNoteWithDetailsById(noteId: string): Promise<NoteWithDetails> {
        try {
            // Check if this is a temporary ID (starts with 'temp_')
            if (noteId.startsWith('temp_')) {
                console.log('Temporary ID detected, returning empty note');
                return {} as NoteWithDetails; // Return empty object for temporary IDs
            }

            const { data, error } = await supabase
                .from('note')
                .select(
                    `
                        *,
                        goal:goal_id (
                            id, title, content, team_id,
                            team:team_id (
                            id, name, profile_image_url
                            )
                        ),
                        todo:todo_id (
                            id, title, complete_date, goal_id,
                            goal:goal_id(
                                team_id,
                                team:team_id(
                                    id, name, profile_image_url
                                )
                            )
                        ),
                        register:register_id (
                            id, name, profile_image_url
                        )
                    `
                )
                .eq('id', noteId)
                .single();

            if (error) throw error;


            const resultData: NoteWithDetails = {
                ...data,
                // goal_id가 있는 경우에만 goal 정보 포함
                // data.goal이 null인 경우도 처리
                goal:
                    data.goal_id && data.goal
                        ? {
                              id: data.goal.id,
                              title: data.goal.title,
                          }
                        : undefined,
                // team 정보는 다음과 같이 처리:
                // 1. goal_id가 있으면 goal.team 사용
                // 2. todo_id가 있고 todo.goal_id가 있으면 todo.goal.team 사용
                team:
                    data.goal?.team ||
                    (data.todo?.goal ? data.todo.goal.team : undefined),
            };
            return resultData;
        } catch (error) {
            console.error('Failed to get note:', error);
            throw error;
        }
    },

    // 단일 노트 조회
    async getNoteById(noteId: string): Promise<Note> {
        try {
            const { data, error } = await supabase
                .from('note')
                .select('*')
                .eq('id', noteId)
                .maybeSingle();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Failed to get note:', error);
            throw error;
        }
    },

    // Update note
    async updateNote(noteId: string, updates: Partial<Note>): Promise<Note> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('note')
                .update({
                    ...updates,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', noteId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Failed to update note:', error);
            throw error;
        }
    },

    async updateNoteContent(
        noteId: string,
        content: string,
        markdownContent?: string,
        enable = true
    ): Promise<Note> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const updateData: Record<string, unknown> = {
                content,
                enable,
                update_id: user.id,
                update_date: new Date().toISOString(),
            };

            // markdown_content가 제공된 경우에만 추가
            if (markdownContent !== undefined) {
                updateData.markdown_content = markdownContent;
            }

            const { data, error } = await supabase
                .from('note')
                .update(updateData)
                .eq('id', noteId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Failed to update note content:', error);
            throw error;
        }
    },

    // Delete note
    async deleteNote(noteId: string): Promise<void> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { error: activity_log_error } = await supabase
                .from('activity_log')
                .update({
                    action: 'delete',
                })
                .eq('reference_id', noteId);

            const { error } = await supabase
                .from('note')
                .update({
                    enable: false,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', noteId);

            if (activity_log_error || error) throw error;
        } catch (error) {
            console.error('Failed to delete note:', error);
            throw error;
        }
    },

    // Get notes by todo ID
    async getNoteByTodoId(todoId: string): Promise<NoteWithDetails[]> {
        try {
            const { data, error } = await supabase
                .from('note')
                .select(
                    `
                  *,
                  todo:todo_id (
                    id, title, complete_date, goal_id,
                    goal:goal_id(
                        team_id,
                        team:team_id(
                            id, name, profile_image_url
                        )
                    )
                ),
                  register:register_id (
                    id, name, profile_image_url
                  )
                `
                )
                .eq('todo_id', todoId)
                .eq('enable', true)
                .order('register_date', { ascending: false });

            if (error) throw error;
            const resultData: NoteWithDetails[] = data.map(note => ({
                ...note,
                todo: note.todo
                    ? {
                          ...note.todo,
                          team: note.todo.goal?.team
                              ? {
                                    id: note.todo.goal.team.id,
                                    name: note.todo.goal.team.name,
                                    profile_image_url:
                                        note.todo.goal.team.profile_image_url,
                                }
                              : undefined,
                      }
                    : undefined,
            }));
            return resultData;
        } catch (e) {
            console.error('Failed to get notes by todo Id: ', e);
            throw e;
        }
    },

    // 노트 공유 링크 생성 또는 업데이트
    async createShareLink(data: ShareLinkData): Promise<Note> {
        try {
            // 단축 URL 코드 생성 (6자리 영숫자)
            const generateShortCode = (): string => {
                const characters =
                    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
                let shortCode = '';
                for (let i = 0; i < 6; i++) {
                    shortCode += characters.charAt(
                        Math.floor(Math.random() * characters.length)
                    );
                }
                return shortCode;
            };

            const shortUrlCode = generateShortCode();
            const now = new Date().toISOString();

            // 노트 테이블 업데이트
            const { data: updatedNote, error } = await supabase
                .from('note')
                .update({
                    short_url_code: shortUrlCode,
                    original_url: data.originalUrl,
                    share_enabled: true,
                    share_created_at: now,
                    update_date: now,
                })
                .eq('id', data.noteId)
                .select()
                .single();

            if (error) throw error;
            return updatedNote;
        } catch (error) {
            console.error('Failed to create share link:', error);
            throw error;
        }
    },

    // 공유 링크로 노트 조회
    async getNoteByShortCode(shortCode: string): Promise<Note | null> {
        try {
            const { data, error } = await supabase
                .from('note')
                .select('*')
                .eq('short_url_code', shortCode)
                .eq('share_enabled', true)
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    // 결과가 없는 경우
                    return null;
                }
                throw error;
            }
            return data;
        } catch (error) {
            console.error('Failed to get note by short code:', error);
            throw error;
        }
    },

    // 공유 링크 비활성화
    async disableShareLink(noteId: string): Promise<Note> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('note')
                .update({
                    share_enabled: false,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', noteId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Failed to disable share link:', error);
            throw error;
        }
    },

    // 노트 조회 기록 추가
    async addNoteViewHistory(noteId: string): Promise<void> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) {
                console.log(
                    '노트 조회 기록 추가 실패: 로그인된 사용자가 없습니다.'
                );
                return;
            }

            const { error } = await supabase.from('note_view_history').upsert(
                {
                    note_id: noteId,
                    user_id: user.id,
                    viewed_at: new Date().toISOString(),
                },
                {
                    onConflict: 'note_id, user_id',
                    ignoreDuplicates: false,
                }
            );

            if (error) {
                console.error('노트 조회 기록 추가 실패:', error);
                throw error;
            }

            console.log('노트 조회 기록 추가 성공:', {
                noteId,
                userId: user.id,
            });
        } catch (error) {
            console.error('노트 조회 기록 추가 실패:', error);
        }
    },
    
    // 마크다운만 업데이트하는 함수
    async updateNoteMarkdownOnly(
        noteId: string,
        markdownContent: string
    ): Promise<Note> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('note')
                .update({
                    markdown_content: markdownContent,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', noteId)
                .select()
                .single();

            if (error) throw error;
            if (!data) throw new Error('노트 마크다운 업데이트 실패');

            return data;
        } catch (error) {
            console.error('노트 마크다운 업데이트 중 오류 발생:', error);
            throw error;
        }
    },
};
